import { useMemo } from "react";
import {
  BUBBLE_HEIGHT,
  BUBBLE_VERTICAL_GAP,
  CHART_RIGHT_PADDING,
  POINTER_HEIGHT,
  POINTER_WIDTH,
} from "shared/components/dataMarker/markerConstants";

export interface MarkerGeometry {
  bubbleLeft: number;
  bubbleTop: number;
  bubbleRight: number;
  bubbleBottom: number;
  bubbleCenterX: number;
  edgeOffset: number;
  pointerBaseY: number;
  pointerApexX: number;
  pointerApexY: number;
  pointerBaseLeftX: number;
  pointerBaseRightX: number;
}

export function useMarkerGeometry({
  cx,
  cy,
  contentWidth,
  chartWidth,
}: {
  cx: number;
  cy: number;
  contentWidth: number;
  chartWidth: number;
}): MarkerGeometry {
  return useMemo(() => {
    const bubbleLeftUnadjusted = cx - contentWidth / 2;
    let edgeOffset = 0;

    if (bubbleLeftUnadjusted + contentWidth > chartWidth) {
      edgeOffset =
        bubbleLeftUnadjusted + contentWidth - chartWidth - CHART_RIGHT_PADDING;
    }

    const bubbleLeft = bubbleLeftUnadjusted - edgeOffset / 1.45;
    const bubbleTop = cy - BUBBLE_HEIGHT - POINTER_HEIGHT - BUBBLE_VERTICAL_GAP;
    const bubbleRight = bubbleLeft + contentWidth;
    const bubbleBottom = bubbleTop + BUBBLE_HEIGHT;
    const bubbleCenterX = cx;

    const pointerBaseY = bubbleBottom - 1;
    const pointerHalfBase = POINTER_WIDTH / 2;
    const pointerApexX = cx;
    const pointerApexY = bubbleBottom + POINTER_HEIGHT;
    let pointerBaseLeftX = cx - pointerHalfBase;
    let pointerBaseRightX = cx + pointerHalfBase;

    // Keep pointer base width constant, but clamp inside the bubble when near chart edges
    if (pointerBaseLeftX < bubbleLeft) {
      const shift = bubbleLeft - pointerBaseLeftX;
      pointerBaseLeftX += shift;
      pointerBaseRightX += shift;
    }
    if (pointerBaseRightX > bubbleRight) {
      const shift = pointerBaseRightX - bubbleRight;
      pointerBaseLeftX -= shift;
      pointerBaseRightX -= shift;
    }

    return {
      bubbleLeft,
      bubbleTop,
      bubbleRight,
      bubbleBottom,
      bubbleCenterX,
      edgeOffset,
      pointerBaseY,
      pointerApexX,
      pointerApexY,
      pointerBaseLeftX,
      pointerBaseRightX,
    };
  }, [cx, cy, contentWidth, chartWidth]);
}

export function buildPointerPath(geometry: MarkerGeometry) {
  return `
    M${geometry.pointerBaseLeftX},${geometry.pointerBaseY}
    L${geometry.pointerBaseRightX},${geometry.pointerBaseY}
    Q${geometry.bubbleCenterX},${geometry.pointerBaseY + (geometry.pointerApexY - geometry.pointerBaseY) * 1} ${geometry.pointerApexX},${geometry.pointerApexY}
    Q${geometry.bubbleCenterX},${geometry.pointerBaseY + (geometry.pointerApexY - geometry.pointerBaseY) * 1} ${geometry.pointerBaseLeftX},${geometry.pointerBaseY}
    Z
  `;
}
